// External
import { retry, map, catchError, timeout, skipWhile } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tv4 } from 'tv4';

// Internal
import { UtilsCommonService } from '../../../utils/utils-common.service';
import { ValuesService } from '../../../values/values.service';
import { schemas } from '../../../models/schemas';
import { ConfigService, LogoutType } from '../../../config/config.service';
import { UsefulService } from '../useful/useful.service';
import { MessageService } from '../../core/message.service';
import { PrivacyEvents } from 'src/app/common/values/privacy.values.service';
import { LoginLinksService } from '../loginLinks/loginLinks.service';
import { ConnectWebmailRegistrationServiceErrorSubCodes } from '../../requests/connect-webmailregistration/connect-webmailregistration.service';

export const POST = 'POST';

export enum Errors {
    ERROR = 'error',
    INVALID_PARAMS = 'Invalid params',
}

export enum Methods {
    REGISTER_INBOX = 'register_inbox',
}

export enum Services {
    CONNECT_WEBMAIL_PROTECTION = 'connect/webmail_protection',
}

@Injectable({
    providedIn: 'root'
})

export class RequestsService {
    errors = {
        request_status_error: 100,
        wrong_host: 101,
        wrong_service: 102,
        wrong_method: 103,
        wrong_params: 104,
        service_error: 105
    };
    markToUpdateLogout = true;
    private readonly onlistLogout$: BehaviorSubject<string> = new BehaviorSubject<string>(this.valuesService.processServiceState.WAITING);
    private readonly privacyErrorCodes = [32701, 40011, 40012, 40021, 40022, 40023, 40031, 40032, 40033, 40034, 40035, 40037, 40041, 40042, 40051, 40052];

    private readonly httpErrorResponseMessage = 'HttpErrorResponse';

    private readonly dataErrorCodesNoRetry = {
        32001: true, // referrral page - invalid parameters
        32005: new Set([
            1108, // devices error
            ...this.privacyErrorCodes,
            39001, // referral - campaign error ex.: expired campaign
            39120, // profile limit exceeded
            ConnectWebmailRegistrationServiceErrorSubCodes.WRONG_CREDENTIALS,
            ConnectWebmailRegistrationServiceErrorSubCodes.INBOX_ALREADY_REGISTERED,
            ConnectWebmailRegistrationServiceErrorSubCodes.NO_FREE_SLOTS
        ])
    };

    constructor(
        @Inject(DOCUMENT) private readonly document: any,
        private readonly cookieService: CookieService,
        private readonly http: HttpClient,
        private readonly utilsCommonService: UtilsCommonService,
        private readonly valuesService: ValuesService,
        private readonly configService: ConfigService,
        private readonly usefulService: UsefulService,
        private readonly messageService: MessageService,
        private readonly loginLinksService  : LoginLinksService

    ) {}

    deleteAllCookies() {
        const lang = this.cookieService.get(this.valuesService.cookieLang);
        const cookieBotConsent = this.cookieService.get(this.valuesService.cookieCookieBotConsent);

        const usedCookies = [
            this.valuesService.cookieToken,
            this.valuesService.cookieTempToken,
            this.valuesService.cookieEmail,
            this.valuesService.cookieShowAr,
            this.valuesService.cookieUser,
            this.valuesService.cookieShowOffers,
            this.valuesService.cookieShowInvoices
        ];

        for (const cookie of usedCookies) {
            try {
                this.cookieService.delete(cookie, '/');
            } catch { }
        }

        try {
            this.cookieService.deleteAll('/', window.location.hostname);
        } catch { }

        try {
            this.cookieService.deleteAll('/', this.configService.getCookiesDomain());
        } catch { }

        try {
            this.cookieService.deleteAll();
        } catch { }

        if (lang) {
            this.cookieService.set(this.valuesService.cookieLang, lang, null, '/', "", true);
        }

        if (cookieBotConsent) {
            this.cookieService.set(this.valuesService.cookieCookieBotConsent, cookieBotConsent, null, '/', "", true);
        }
    }

    /**
     * Clears all cookies
     * Used also in auth guard
     */
    clearCookies() {
        this.deleteAllCookies();
        sessionStorage.clear();
        localStorage.clear();
    }

    clearAll(logoutType: LogoutType = LogoutType.NONE) {
        this.clearCookies();
        // navigates to login page if no token
        const path = window.location.pathname.concat(window.location.search);
        if (logoutType !== LogoutType.NO_REDIRECT) {
            window.location.href = this.loginLinksService.loginPage(true, true, path, logoutType);
        }
    }

    logout(logoutType: LogoutType = LogoutType.NONE) {
        if (!this.markToUpdateLogout) {
            return;
        }

        if (this.onlistLogout$.value === this.valuesService.processServiceState.INPROGRESS) {
            return this.onlistLogout$.asObservable()
                .subscribe().add(
                    () => {
                        skipWhile(res => res !== this.valuesService.processServiceState.DONE);
                    }
                );
        } else {
            this.onlistLogout$.next(this.valuesService.processServiceState.INPROGRESS);
            const tkn = this.cookieService.get(this.valuesService.cookieToken);
            const temptkn = this.cookieService.get(this.valuesService.cookieTempToken);
            if (tkn || temptkn) {
                const json = [];
                if (tkn) {
                    json.push({
                        id: parseInt((Math.random() * 100).toString(), 10),
                        jsonrpc: this.valuesService.jsonrpc,
                        method: 'logout',
                        params: {
                            connect_source: {
                                user_token: this.cookieService.get(this.valuesService.cookieToken)
                            }
                        }
                    });
                }

                if (temptkn) {
                    json.push({
                        id: parseInt((Math.random() * 100).toString(), 10),
                        jsonrpc: this.valuesService.jsonrpc,
                        method: 'logout',
                        params: {
                            connect_source: {
                                user_token: this.cookieService.get(this.valuesService.cookieTempToken)
                            }
                        }
                    });
                }

                this.make(this.configService.config.nimbusServer, this.valuesService.loginMgmtService, json, 'POST')
                    .subscribe().add(
                        () => {
                            this.clearAll(logoutType);
                            this.onlistLogout$.next(this.valuesService.processServiceState.DONE);
                            this.markToUpdateLogout = false;
                        }
                    );
            } else {
                this.clearAll(logoutType);
                this.onlistLogout$.next(this.valuesService.processServiceState.DONE);
                this.markToUpdateLogout = false;
            }
        }
    }

    shouldLogout(error) {
        if (error?.error?.code === 32004 && error?.error?.message === "Wrong credentials") {
            this.clearAll(LogoutType.SIMPLE);
        }
    }

    //*
    //* HELPER FUNCTIONS
    //*

    /**
    * This method will check response from server and decide if there are good errors
    * @param {string} service Connect service
    * @param {string} method Connect service method
    * @returns {nothing} Redirects to /500
    */
    redirect500(service, method) {
        let mandatory_services = {
            [this.valuesService.subscriptionMgmtService]: 'list',
            [this.valuesService.connectMgmtService]: 'list_devices_v2',
            [this.valuesService.userinfoMgmtService]: 'getInfo'
        }

        if (mandatory_services.hasOwnProperty(service) && method === mandatory_services[service]) {
            this.document.location.href = this.valuesService.centralPaths["500"].path;
        }
    };

    /**
    * This method will check response from server and decide if there are good errors
    * Returns 'false' if retry is not needed for the error or throws error if retry is needed 
    * @param {object} data Contains full response from server
    * @returns {boolean} false if no error / error if exists
    */
    handleConnectError(data, service, method) {
        if (!data.error && !(data.status && data.status === 'error')) {
            return false;
        }

        if (service === Services.CONNECT_WEBMAIL_PROTECTION && method === Methods.REGISTER_INBOX) {
            return false;
        }

        const dataErrorCode = data?.error?.code;
        const dataErrorDataCode = data?.error?.data?.code;

        this.shouldLogout(data);

        if (dataErrorCode) {
            const dataErrorCodeNoRetry = this.dataErrorCodesNoRetry[dataErrorCode];
            if (dataErrorCodeNoRetry === true) {
                return false;
            }

            if (dataErrorCodeNoRetry.has(dataErrorDataCode)) {
                return false;
            }
        }

        let mandatory_services = {
            [this.valuesService.subscriptionMgmtService]: 'list',
            [this.valuesService.connectMgmtService]: 'list_devices',
            [this.valuesService.userinfoMgmtService]: 'getInfo'
        }

        if (this.usefulService.checkNested(data, "status") && data.status === 'error') {
            throw {
                status: 'error',
                code: this.errors.service_error,
                message: data.message,
                internal_code: data.code
            };
        } else if (mandatory_services.hasOwnProperty(service) && method === mandatory_services[service]) {
            this.document.location.href = this.valuesService.centralPaths["500"].path;
        } else {
            throw {
                status: 'error',
                code: this.errors.service_error,
                message: data.error.message,
                internal_code: data.error.code,
                internal_data: data.error.data
            };
        }
    }

    successRequest(data, service, params, method) {
        if (this.utilsCommonService.checkArray(data)) { // avem un batch request (deci un array de obiecte) nu un obiect cu raspunsul.
            let errs = false;
            let call;
            for (let i = 0; i < data.length; i++) {
                this.shouldLogout(data);
                if (data[i].error || data[i].status === 'error') {
                    if (data[i].status && data[i].status === 'error') {
                        errs = true;
                        call = { 
                            status: 'error', 
                            code: this.errors.service_error, 
                            message: data[i].message, 
                            internal_code: data[i].code 
                        };
                    } else if (data[i].error && data[i].error.code === 32005 &&
                                (data[i].error.data === 'Could not find setting' 
                                || data[i].error.data.code === 1108 
                                || data[i].error.data.code === 1128 
                                || data[i].error.data.code === 1129
                                || data[i].error.data.message === 'Article ID not found!' 
                                || data[i].error.data.code === 2109
                                || data[i].error.data.code === 32701)) {
                        // nu trebuie sa facem nimic, astea sunt errs bune
                        // errors bune (2109, Article ID not found!) din array-ul de articles din security news - activity page
                    } else {
                        errs = true;
                        call = { 
                            status: 'error', 
                            code: this.errors.service_error, 
                            message: data[i].error.message, 
                            internal_code: data[i].error.code, 
                            internal_data: data[i].error.data,
                            id: data[i].id 
                        };
                    }
                }
            }
            if (errs === false) {
                return data;
            } else {
                this.redirect500(service, params[0].method);
                return call;
            }
        } else {
            let aux = this.handleConnectError(data, service, params.method);
            if (aux) {
                return aux;
            } else {
                if (this.usefulService.checkNested(data, "error", "code") && data.error.code === 32005
                    && this.usefulService.checkNested(data, "error", "data", "code") 
                    && (this.privacyErrorCodes.indexOf(data.error.data.code) != -1)) {
                    // when an error happens in privacy, chances are the subscription has expired
                    // so th site should be updated because sometimes dip is standalone
                    this.messageService.sendMessage(PrivacyEvents.SUBSCRIPTION_EXPIRED, { id: 'val', data: {} });
                    return {
                        status: 'error',
                        code: data.error.data.code,
                        message: data.error.data.message,
                        internal_code: data.error.code,
                        data: data.error.data.data,
                        redirect500: false,
                        service: null,
                        method: null
                    };
                }
                //! TBD De verificat daca aici scoatem resultul sau in urmatorul layer
                return data;
            }
        }
    };

    errorRequest(err, info) {
        if (err.data === null && err.status === 0) {
            err.data = 'Timeout (10 sec)';
            err.status = 408;
        }
        // Experimental - log to server
        let complete_data: any = {};
        complete_data.status = status;
        complete_data.data = err.data;

        if (this.utilsCommonService.checkArray(err)) { // avem un batch request (deci un array de obiecte) nu un obiect cu raspunsul.
            return { 
                status: 'error', 
                code: this.errors.request_status_error, 
                message: err.data, 
                internal_code: status, 
                redirect500: true, 
                service: info.service, 
                method: info.method 
            };
        } else {
            if (err.data) {
                return { 
                    status: 'error', 
                    code: this.errors.request_status_error, 
                    message: err.data, 
                    internal_code: status, 
                    redirect500: true, 
                    service: info.service, 
                    method: info.params.method 
                };
            } else {
                return { 
                    status: 'error', 
                    code: this.errors.request_status_error, 
                    message: err, 
                    internal_code: status, 
                    redirect500: true, 
                    service: info.service, 
                    method: info.params.method 
                };
            }
        }
    };

    //*
    //* CORE
    //*
    
    // Metoda care face requestul
    // responseType: 'arraybuffer'|'blob'|'json'|'text', json is default
    make(link, service, params, method, responseType?): Observable<any> {

        //*
        //* HEADERS CUSTOM
        //*
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        };


        if (link.indexOf('nimbus.bitdefender.net') !== -1) {
            httpOptions.headers = httpOptions.headers.set('x-nimbus-clientid', this.configService.config.nimbusClientId );
        }

        if (responseType) {
            httpOptions['responseType'] = responseType;
        }

        //*
        //* VALIDATIONS
        //*

        if (!tv4.validate(link, schemas.schemaURL)) {
            return of({ 
                status: 'error', 
                code: this.errors.wrong_host, 
                message: tv4.error.message, 
                internal_code: tv4.error.code 
            });
        }

        if (!tv4.validate(service, schemas.schemaSERVICE)) {
            return of({ 
                status: 'error', 
                code: this.errors.wrong_service, 
                message: tv4.error.message, 
                internal_code: tv4.error.code
            });
        }

        if (!tv4.validate(method, schemas.schemaMETHOD)) {
            return of({ 
                status: 'error', 
                code: this.errors.wrong_method, 
                message: tv4.error.message, 
                internal_code: tv4.error.code 
            });
        }

        if (!tv4.validate(params, schemas.schemaJSONorARRAY)) {
            return of({ 
                status: 'error', 
                code: this.errors.wrong_params, 
                message: tv4.error.message, 
                internal_code: tv4.error.code 
            });
        }

        if (method === 'POST') {
            let configPost = {
                url: link + service,
                data: params
            };

            return this.http.post(configPost.url, configPost.data, httpOptions)
            .pipe(
                timeout(10000), //* timeout of request 10 seconds
                map(
                    (res: any) => {
                        return this.successRequest(res, service, params, method);
                    }
                ),
                retry({ delay: (error, retryCounter) => {

                    // If the error is different from 'HttpErrorResponse' request will be retried one more time
                    // Otherwise, the request will not be retried and an error will be thrown in order to stop retrying
                    if (error?.name !== this.httpErrorResponseMessage && retryCounter === 1) {
                        return of(error);
                    }

                    throw error;
                }}),
                catchError(err => {
                    throw this.errorRequest(err, { service: service, params: params });
                })
            );
        } else {
            let conf = {
                url: link + service + '?' //+ param(params)
            };

            return this.http.get(conf.url, httpOptions)
            .pipe(
                timeout(10000), //* timeout of request 10 seconds
                map(
                    (res: any) => {
                        return this.successRequest(res, service, params, method);
                    }
                ),
                retry(1), //* retry after request error (! NOT connect error)
                catchError((err) => {
                    throw this.errorRequest(err, { service: service, method: method });
                })
            );
        }

    }

}